import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  Divider,
  Snackbar,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Dialog,
  DialogTitle,
  DialogContent,
  TableContainer,
  TableBody,
  DialogActions,
  Switch,
  Select,
  InputLabel,
  FormControl,
  Autocomplete,
} from '@mui/material';
import { IconButton } from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon, Cancel as CancelIcon, Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, CloudUpload as UploadIcon} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api/api';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import DetalleConsumoCliente from './DetalleConsumoCliente';
import ImportarDteModal from './ImportarDteModal';

// =================================================================
// HELPERS DE FORMATEO
// =================================================================


// Formatear NIT: 06142412871025 → 0614-241287-102-5
const formatNit = (value) => {
  if (!value) return '';
  const digits = String(value).replace(/\D/g, ''); // solo números
  if (digits.length !== 14) return digits; // si no es longitud exacta, mostrar sin formato
  return `${digits.slice(0,4)}-${digits.slice(4,10)}-${digits.slice(10,13)}-${digits.slice(13)}`;
};

// Formatear DUI: 123456789 → 12345678-9
const formatDui = (value) => {
  if (!value) return '';
  const digits = String(value).replace(/\D/g, '');
  if (digits.length !== 9) return digits;
  return `${digits.slice(0,8)}-${digits.slice(8)}`;
};

// Validar formato de email
const validateEmail = (email) => {
  if (!email) return ''; // vacío es válido (opcional)
  const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return regex.test(email) ? '' : 'Formato de email inválido';
};

// =================================================================
// COMPONENTE
// =================================================================



const EditarCliente = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
   

  const [sucursales, setSucursales] = useState([]);

  // Inicializamos TODOS los campos como string vacío o valor por defecto → evita uncontrolled inputs
  const [formData, setFormData] = useState({
    id_sucursal: '',
    id_contribuyente: '',
    nombre_cliente: '',
    email_cliente: '',
    telefonos_cliente: '',
    sexo_cliente: '',
    fecha_nac_cliente: null,
    fecha_ingreso_cliente: null,
    doc_id_cliente: '',
    prefesion: '',
    remitido: '',
    direccion_cliente: '',
    punto_referencia_dc: '',
    observaciones_cliente: '',
    es_enviar_correos: true,
    token_cliente: '',
    createdAt: '',
    updatedAt: '',
    dui: '', // Almacenado sin formato (solo números)
    nit: '', // Almacenado sin formato (solo números)
    nrc: '', // Almacenado sin formato (solo números)
    pasaporte: '',
    carnet_de_residencia: '',
    id_sub_zona: '',
    latitud: '', 
    longitud: '',
  });

  // DOC ERRORS ahora solo para validación de longitud/formato para DUI/NIT
  const [docErrors, setDocErrors] = useState({
    dui: '', // 'El DUI debe tener exactamente 9 dígitos'
    nit: '', // 'El NIT debe tener exactamente 14 dígitos'
    nrc: '', // NRC mantiene la validación numérica del original
  });

  const sexoOptions = [
    { value: '', label: 'No especificado' },
    { value: 1, label: 'Masculino' },
    { value: 0, label: 'Femenino' },
  ];

  const [emailError, setEmailError] = useState('');
  const [subZonas, setSubZonas] = useState([]);   //
 // const [subZonaMap, setSubZonaMap] = useState({});

  const [configsCliente, setConfigsCliente] = useState([]);
  const [openConfigModal, setOpenConfigModal] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [openImportarDte, setOpenImportarDte] = useState(false);
  const [configFormData, setConfigFormData] = useState({
    frecuencia: '',
    dia_preferido: '',
    hora_aprox: '',
    hoja_servicio_fija: '',
    notas_fijas: '',
    etiquetas_fijas: '',
    activo: true,
    ultima_visita_realizada: null,
    // puedes agregar más si quieres (etiquetas_fijas, precio_acordado, etc.)
  });
  const DIAS_SEMANA = [
    { value: '', label: 'Ninguno / No aplica' },
    { value: '1', label: 'Lunes' },
    { value: '2', label: 'Martes' },
    { value: '3', label: 'Miércoles' },
    { value: '4', label: 'Jueves' },
    { value: '5', label: 'Viernes' },
    { value: '6', label: 'Sábado' },
    { value: '7', label: 'Domingo' },
  ];



  // =================================================================
  // HANDLERS PARA DUI y NIT (NUEVOS)
  // =================================================================

  const handleDuiChange = (e) => {
    // Extrae solo los dígitos y limita a 9
    const raw = e.target.value.replace(/\D/g, '').slice(0, 9);
    setFormData(prev => ({ ...prev, dui: raw }));
    // Valida que sean exactamente 9 dígitos si hay algo escrito
    setDocErrors(prev => ({ ...prev, dui: raw && raw.length !== 9 ? 'El DUI debe tener exactamente 9 dígitos' : '' }));
  };

  const handleNitChange = (e) => {
    // Extrae solo los dígitos y limita a 14
    const raw = e.target.value.replace(/\D/g, '').slice(0, 14);
    setFormData(prev => ({ ...prev, nit: raw }));
    // Valida que sean exactamente 14 dígitos si hay algo escrito
    setDocErrors(prev => ({ ...prev, nit: raw && raw.length !== 14 ? 'El NIT debe tener exactamente 14 dígitos' : '' }));
  };

  // Handler para NRC (mantiene la lógica de solo números del original)
  const validateNumericDoc = (value, field) => {
    if (!value) return '';
    if (!/^\d+$/.test(value)) return `El ${field} solo permite números`;
    return '';
  };

  const handleNrcChange = (e) => {
    const value = e.target.value.replace(/\s/g, ''); // Remueve espacios
    setFormData(prev => ({ ...prev, nrc: value }));
    setDocErrors(prev => ({ ...prev, nrc: validateNumericDoc(value, 'NRC') }));
  };

  const handleEmailChange = (e) => {
  const value = e.target.value.trimStart(); // evita espacios al inicio
  setFormData(prev => ({ ...prev, email_cliente: value }));
  setEmailError(validateEmail(value));
  };

  // Reemplaza handleNumericDoc

  // =================================================================
  // FETCH DE DATOS
  // =================================================================

  useEffect(() => {
  if (id) {
    fetchConfigsCliente(id);
  }
}, [id]);

const fetchConfigsCliente = async (clienteId) => {
  try {
    const res = await api.get(`/configuracion_domicilio/?id_cliente=${clienteId}`);
    setConfigsCliente(res.data);
    console.log('[EditarCliente] Configs recurrentes cargadas:', res.data.length);
  } catch (err) {
    console.error('Error cargando configs recurrentes:', err);
    // Opcional: mostrar snackbar de error
    
  }
};

const handleOpenConfigModal = (config = null) => {
  if (config) {
    setConfigFormData({
      ...config,
      frecuencia: config.frecuencia || '',
      dia_preferido: config.dia_preferido || '',
      hora_aprox: config.hora_aprox || '',
      hoja_servicio_fija: config.hoja_servicio_fija || '',
      notas_fijas: config.notas_fijas || '',
      etiquetas_fijas: config.etiquetas_fijas || '',
      activo: config.activo ?? true,
      id_sub_zona: config.id_sub_zona || '',
    });
    setSelectedConfig(config);
  } else {
    setConfigFormData({
      frecuencia: '',
      dia_preferido: '',
      hora_aprox: '',
      hoja_servicio_fija: '',
      notas_fijas: '',
      etiquetas_fijas: '',
      activo: true,
      id_sub_zona: '',
    });
    setSelectedConfig(null);
  }
  setOpenConfigModal(true);
};

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [clienteRes, sucRes] = await Promise.all([
          api.get(`/clientes/${id}`),
          api.get('/sucursales/').catch(() => ({ data: [] }))
        ]);

        const c = clienteRes.data;

        console.log('--- DATOS ORIGINALES DEL CLIENTE ---');
      console.log('ID Cliente:', id);
      console.log('id_sub_zona recibido:', c.id_sub_zona);
      console.log('Nombre cliente:', c.nombre_cliente);

        const normalized = {
          id_sucursal: c.id_sucursal || '',
          id_contribuyente: c.id_contribuyente || '',
          nombre_cliente: c.nombre_cliente || '',
          email_cliente: c.email_cliente || '',
          telefonos_cliente: c.telefonos_cliente || '',
          sexo_cliente: (c.sexo_cliente !== null && c.sexo_cliente !== undefined) ? Number(c.sexo_cliente) : '',
          fecha_nac_cliente: c.fecha_nac_cliente ? dayjs(c.fecha_nac_cliente) : null,
          fecha_ingreso_cliente: c.fecha_ingreso_cliente ? dayjs(c.fecha_ingreso_cliente) : null,
          doc_id_cliente: c.doc_id_cliente || '',
          prefesion: c.prefesion || '',
          remitido: c.remitido || '',
          direccion_cliente: c.direccion_cliente || '',
          punto_referencia_dc: c.punto_referencia_dc || '',
          observaciones_cliente: c.observaciones_cliente || '',
          es_enviar_correos: c.es_enviar_correos !== false,
          token_cliente: c.token_cliente || '',
          createdAt: c.createdAt ? dayjs(c.createdAt).format('DD/MM/YYYY HH:mm') : '',
          updatedAt: c.updatedAt ? dayjs(c.updatedAt).format('DD/MM/YYYY HH:mm') : '',
          dui: c.dui || '',
          nit: c.nit || '',
          nrc: c.nrc || '',
          pasaporte: c.pasaporte || '',
          carnet_de_residencia: c.carnet_de_residencia || '',
          id_sub_zona: c.id_sub_zona || '',
          latitud: c.latitud || '', 
          longitud: c.longitud || '',
          nombre_sub_zona: c.nombre_sub_zona || '',
          nombre_zona: c.nombre_zona || '',
        };

        setFormData(normalized);
        setSucursales(sucRes.data || []);

        // Validar documentos al cargar
        setDocErrors({
          dui: normalized.dui && normalized.dui.length !== 9 ? 'El DUI debe tener exactamente 9 dígitos' : '',
          nit: normalized.nit && normalized.nit.length !== 14 ? 'El NIT debe tener exactamente 14 dígitos' : '',
          nrc: validateNumericDoc(normalized.nrc, 'NRC'),
        });

      } catch (err) {
        setError('Error al cargar el cliente');
        setSnackbar({ open: true, message: 'Error al cargar datos', severity: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);


  useEffect(() => {
  api.get('/sub_zonas/').then(res => {
    setSubZonas(res.data);

    console.log('--- CATÁLOGO DE SUB-ZONAS ---');
      console.log('Total sub-zonas cargadas:', res.data.length);
      console.log('IDs disponibles:', res.data.map(sz => sz.id_sub_zona));

    // Aquí sí puedes usar res.data
    //const subZonaMapTemp = res.data.reduce((map, sz) => {
      //map[sz.id_sub_zona] = sz.nombre_sub_zona;
      //return map;
    //}, {});
    //setSubZonaMap(subZonaMapTemp);

    console.log('[EditarCliente] Sub-zonas y mapa cargados:', res.data.length);
  }).catch(err => console.error('Error cargando sub-zonas:', err));
}, []);

  // =================================================================
  // LÓGICA DE GUARDADO
  // =================================================================

  const handleSave = async () => {
  // 1. Validaciones iniciales
  if (!formData.nombre_cliente.trim()) {
    setError('El nombre es obligatorio');
    return;
  }

  if (formData.dui && formData.dui.length !== 9) {
    setError('El DUI debe tener exactamente 9 dígitos');
    return;
  }
  if (formData.nit && formData.nit.length !== 14) {
    setError('El NIT debe tener exactamente 14 dígitos');
    return;
  }
  if (docErrors.nrc || docErrors.dui || docErrors.nit) {
    setError('Corrija los errores en los documentos de identidad');
    return;
  }

  try {
    setSaving(true);
    setError('');

    const payload = {
      id_sucursal: formData.id_sucursal || null,
      id_contribuyente: formData.id_contribuyente || null,
      nombre_cliente: formData.nombre_cliente.trim(),
      email_cliente: formData.email_cliente || null,
      telefonos_cliente: formData.telefonos_cliente || null,
      sexo_cliente: (formData.sexo_cliente === 0 || formData.sexo_cliente === 1) ? formData.sexo_cliente : null,
      fecha_nac_cliente: formData.fecha_nac_cliente ? formData.fecha_nac_cliente.format('YYYY-MM-DD') : null,
      doc_id_cliente: formData.doc_id_cliente || null,
      prefesion: formData.prefesion || null,
      remitido: formData.remitido || null,
      direccion_cliente: formData.direccion_cliente || null,
      punto_referencia_dc: formData.punto_referencia_dc || null,
      observaciones_cliente: formData.observaciones_cliente || null,
      es_enviar_correos: formData.es_enviar_correos,
      dui: formData.dui || null,
      nit: formData.nit || null,
      nrc: formData.nrc || null,
      pasaporte: formData.pasaporte || null,
      carnet_de_residencia: formData.carnet_de_residencia || null,
      id_sub_zona: formData.id_sub_zona ? parseInt(formData.id_sub_zona) : null,
    };

    // Petición al API
    await api.put(`/clientes/${id}`, payload);

    // NOTIFICACIÓN: Se muestra el mensaje pero NO se navega a ningún lado
    setSnackbar({ 
      open: true, 
      message: '¡Cambios guardados correctamente!', 
      severity: 'success' 
    });

  } catch (err) {
    const msg = err.response?.data?.detail || err.message || 'Error al guardar';
    setError(msg);
    setSnackbar({ open: true, message: msg, severity: 'error' });
  } finally {
    setSaving(false);
  }
};

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
      <CircularProgress size={60} />
    </Box>
  );


const handleSaveConfig = async () => {
  // 1. Aseguramos que los tipos de datos coincidan con el backend (int)
  const data = {
    id_cliente: parseInt(id), // Convertimos el ID de la URL a entero
    frecuencia: configFormData.frecuencia,
    dia_preferido: configFormData.dia_preferido ? parseInt(configFormData.dia_preferido) : null,
    hora_aprox: configFormData.hora_aprox || null,
    hoja_servicio_fija: configFormData.hoja_servicio_fija.trim() || '',
    notas_fijas: configFormData.notas_fijas.trim() || '',
    etiquetas_fijas: configFormData.etiquetas_fijas.trim(),
    activo: Boolean(configFormData.activo), // Aseguramos que sea booleano
    id_sub_zona: configFormData.id_sub_zona ? parseInt(configFormData.id_sub_zona) : null,
  };

  console.log('--- ENVIANDO CONFIGURACIÓN ---');
  console.log('Payload:', data);

  try {
    if (selectedConfig) {
      // PUT requiere el ID en la URL
      await api.put(`/configuracion_domicilio/${selectedConfig.id_config}`, data);
      showMessage('Configuración actualizada');
    } else {
      // POST envía todo en el body
      await api.post('/configuracion_domicilio/', data);
      showMessage('Nueva configuración creada correctamente');
    }

    fetchConfigsCliente(id); 
    setOpenConfigModal(false);
    
    setSnackbar({ 
      open: true, 
      message: 'Configuración guardada exitosamente', 
      severity: 'success' 
    });
  
  } catch (err) {
    console.error('Error guardando config:', err);
    const errorMsg = err.response?.data?.detail || 'Error al guardar configuración';
    
    // Usamos showMessage para errores también
    showMessage(typeof errorMsg === 'string' ? errorMsg : 'Error de validación', 'error');
  }
};

  // Valores mostrados formateados (para TextField)
  const displayedDui = formatDui(formData.dui);
  const displayedNit = formatNit(formData.nit);
  // NRC no lleva formato especial, solo es numérico

  const handleDeleteConfig = async (id_config) => {
  if (!window.confirm('¿Eliminar esta configuración recurrente?')) return;
  try {
    await api.delete(`/configuracion_domicilio/${id_config}`);
    setConfigsCliente(prev => prev.filter(c => c.id_config !== id_config));
    // Opcional: snackbar
  } catch (err) {
    console.error('Error eliminando config:', err);
    // snackbar error
  }
};

const handleCheckAndOpenModal = () => {
  // El nombre correcto de tu estado es configsCliente
  if (configsCliente && configsCliente.length > 0) {
    setSnackbar({
      open: true,
      message: 'Este cliente ya tiene una configuración recurrente asignada.',
      severity: 'warning' 
    });
    return; // Bloquea la apertura del modal
  }

  // Si está vacío, abre el modal para crear la primera
  handleOpenConfigModal();
};

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

 const showMessage = (msg, severity = 'success') => {
  setSnackbar({
    open: true,
    message: msg,
    severity: severity
  });
};

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
      <Box sx={{ bgcolor: 'grey.50', minHeight: '100vh', py: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 2 }}>
            <Button startIcon={<ArrowBackIcon />} variant="outlined" onClick={() => navigate(`/clientes/${id}`)}>
              Regresar
            </Button>
            <Typography variant="h4" fontWeight="bold">Editar Cliente</Typography>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

          <Paper elevation={4} sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <Box sx={{ p: { xs: 3, md: 5 } }}>

              {/* === DATOS PERSONALES === */}
              <Typography variant="h6" color="primary" gutterBottom>Datos Personales</Typography>
              <Grid container spacing={3} >
              
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    label="Sucursal"
                    fullWidth
                    value={formData.id_sucursal}
                    onChange={e => setFormData({ ...formData, id_sucursal: e.target.value })}
                    sx={{
                      bgcolor: '#e8f5e9', // Verde claro suave (puedes probar con '#f0fdf4' o '#e8f8f5')
                      borderRadius: 1,
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#4caf50', // Borde verde al pasar el mouse
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#2e7d32', // Borde verde oscuro al enfocar
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#2e7d32', // Color de la etiqueta encendida
                      },
                    }}
                  >
                    {sucursales.map(s => (
                      <MenuItem key={s.id_sucursal} value={s.id_sucursal}>{s.nombre_sucursal}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField label="Nombre completo *" fullWidth value={formData.nombre_cliente}
                    onChange={e => setFormData({ ...formData, nombre_cliente: e.target.value })}
                    error={!formData.nombre_cliente.trim() && !saving} // Validación simple de nombre
                    helperText={!formData.nombre_cliente.trim() && !saving ? 'Campo obligatorio' : ''}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField select label="Sexo" fullWidth value={formData.sexo_cliente !== null && formData.sexo_cliente !== undefined ? formData.sexo_cliente : ''}
                    onChange={e => setFormData({ ...formData, sexo_cliente: e.target.value })}>
                  {/*{sexoOptions.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}*/}
                    {sexoOptions.map(o => ( <MenuItem key={o.value} value={o.value}> {o.label} </MenuItem> ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                 <DatePicker
  label="Fecha nacimiento"
  value={formData.fecha_nac_cliente}
  onChange={v => setFormData({ ...formData, fecha_nac_cliente: v })}
  openTo="year" // Abre directamente en la selección de años
  views={['year', 'month', 'day']} // Flujo intuitivo para cumpleaños
  slotProps={{
    textField: { fullWidth: true },
  }}
/>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField label="Fecha ingreso" fullWidth disabled
                    value={formData.fecha_ingreso_cliente?.format('DD/MM/YYYY') || ''} />
                </Grid>
              </Grid>

              <Divider sx={{ my: 4 }} />

              {/* === DOCUMENTOS === */}
              <Typography variant="h6" color="primary" gutterBottom>Documentos de Identidad</Typography>
              <Grid container spacing={3}>
                
                {/* DUI con formato y handler nuevo */}
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="DUI"
                    fullWidth
                    value={displayedDui}
                    onChange={handleDuiChange}
                    error={!!docErrors.dui}
                    helperText={docErrors.dui || 'Formato: 12345678-9 (9 dígitos)'}
                    placeholder="12345678-9"
                    inputProps={{ maxLength: 10 }} // Limita la entrada formateada a 10 caracteres (8+guion+1)
                  />
                </Grid>

                {/* NIT con formato y handler nuevo */}
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="NIT"
                    fullWidth
                    value={displayedNit}
                    onChange={handleNitChange}
                    error={!!docErrors.nit}
                    helperText={docErrors.nit || 'Formato: 0614-241287-102-5 (14 dígitos)'}
                    placeholder="0614-241287-102-5"
                    inputProps={{ maxLength: 17 }} // Limita la entrada formateada a 17 caracteres (4+1+6+1+3+1+1)
                  />
                </Grid>

                {/* NRC con handler de solo números (adaptado) */}
                <Grid item xs={12} sm={4} >
                  <TextField
                    label="NRC"
                    fullWidth
                    value={formData.nrc}
                    onChange={handleNrcChange}
                    error={!!docErrors.nrc}
                    helperText={docErrors.nrc || 'Solo números'}
                    inputProps={{ maxLength: 10 }} // Mantenemos un máximo de 10
                  />
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                  <TextField label="Pasaporte" fullWidth value={formData.pasaporte}
                    onChange={e => setFormData({ ...formData, pasaporte: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField label="Carnet Residencia" fullWidth value={formData.carnet_de_residencia}
                    onChange={e => setFormData({ ...formData, carnet_de_residencia: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField label="Otro documento" fullWidth value={formData.doc_id_cliente}
                    onChange={e => setFormData({ ...formData, doc_id_cliente: e.target.value })} />
                </Grid>
              </Grid>

              <Divider sx={{ my: 4 }} />

              {/* === CONTACTO === */}
              <Typography variant="h6" color="primary" gutterBottom>Contacto y Dirección</Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <TextField
                      label="Email"
                      type="email"
                      fullWidth
                      value={formData.email_cliente}
                      onChange={handleEmailChange}
                      error={!!emailError}
                      helperText={emailError || 'Ejemplo: cliente@ejemplo.com (opcional)'}
                      placeholder="cliente@ejemplo.com"
                    />
                  </Grid>
                <Grid item xs={12} md={6}>
                  <TextField label="Teléfonos" fullWidth value={formData.telefonos_cliente}
                    onChange={e => setFormData({ ...formData, telefonos_cliente: e.target.value })} />
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Dirección" multiline rows={2} fullWidth value={formData.direccion_cliente}
                    onChange={e => setFormData({ ...formData, direccion_cliente: e.target.value })} />
                </Grid>
                {/* === ZONA Y SUB-ZONA (Solo lectura) === */}
                <Grid item xs={12} md={6}>
                    <TextField
                        label="Zona"
                        fullWidth
                        value={formData.nombre_zona || '—'}
                        disabled
                        InputProps={{ readOnly: true }}
                        helperText="Zona principal"
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Autocomplete
                      options={subZonas}
                      getOptionLabel={(option) => option.nombre_sub_zona || ""}
                      // Compara el ID del objeto con el valor en el estado
                      value={subZonas.find((sz) => sz.id_sub_zona === formData.id_sub_zona) || null}
                      onChange={(event, newValue) => {
                        setFormData({ 
                          ...formData, 
                          id_sub_zona: newValue ? newValue.id_sub_zona : '' 
                        });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Sub-zona Logística"
                          helperText="Busca y asigna la zona principal para servicios a domicilio"
                          variant="outlined"
                        />
                      )}
                      // No mostrar mensaje de "No hay opciones" si está cargando
                      noOptionsText="No se encontraron zonas"
                      fullWidth
                    />
                </Grid>

                {formData.latitud && formData.longitud && (
                  <Grid item xs={12} sx={{ mt: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      color="secondary"
                      startIcon={<span role="img" aria-label="pin">📍</span>}
                      onClick={() => window.open(`https://www.google.com/maps?q=${formData.latitud},${formData.longitud}`, '_blank')}
                    >
                      Ver en Google Maps
                    </Button>
                    <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.secondary' }}>
                      Ubicación GPS registrada para este cliente.
                    </Typography>
                  </Grid>
                )}
              
                <Grid item xs={12} md={8}>
                  <TextField label="Punto de referencia" fullWidth value={formData.punto_referencia_dc}
                    onChange={e => setFormData({ ...formData, punto_referencia_dc: e.target.value })} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={<Checkbox checked={formData.es_enviar_correos}
                      onChange={e => setFormData({ ...formData, es_enviar_correos: e.target.checked })} />}
                    label="Recibir correos"
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 4 }} />

              {/* === OTROS === */}
              <Typography variant="h6" color="primary" gutterBottom>Información Adicional</Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField label="Profesión" fullWidth value={formData.prefesion}
                    onChange={e => setFormData({ ...formData, prefesion: e.target.value })} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField label="Remitido por" fullWidth value={formData.remitido}
                    onChange={e => setFormData({ ...formData, remitido: e.target.value })} />
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Observaciones" multiline rows={4} fullWidth value={formData.observaciones_cliente}
                    onChange={e => setFormData({ ...formData, observaciones_cliente: e.target.value })} />
                </Grid>
              </Grid>

              

              <Divider sx={{ my: 4 }} />


              {/* === INFO SISTEMA === */}
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <TextField label="Creado" fullWidth disabled value={formData.createdAt} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField label="Actualizado" fullWidth disabled value={formData.updatedAt} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField label="Token" fullWidth disabled value={formData.token_cliente || '—'} />
                </Grid>
              </Grid>

              {/* BOTONES */}
              <Box sx={{ mt: 5, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button variant="outlined" size="large" startIcon={<CancelIcon />}
                  onClick={() => navigate(`/clientes/${id}`)}>
                  Cancelar
                </Button>
                <Button variant="contained" size="large" startIcon={<SaveIcon />}
                  onClick={handleSave} disabled={saving || !!docErrors.dui || !!docErrors.nit || !!docErrors.nrc}>
                  {saving ? <CircularProgress size={24} color="inherit" /> : 'Guardar'}
                </Button>
              </Box>
            </Box>
          </Paper>

       
<Box sx={{ mt: 8, mb: 4 }}>
  <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
    Configuraciones Recurrentes de Domicilios
  </Typography>
  <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
    Patrones automáticos de domicilios para este cliente (baño mensual, vacunas, etc.).
  </Typography>

  <Button
    variant="contained"
    startIcon={<AddIcon />}
    onClick={handleCheckAndOpenModal}
    sx={{ mb: 3 }}
  >
    Nueva Configuración Recurrente
  </Button>

  <TableContainer component={Paper}>
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>ID</TableCell>    
          <TableCell>Frecuencia</TableCell>      
          <TableCell>Día Preferido</TableCell>
          <TableCell>Hora Aprox</TableCell>
          <TableCell>Hoja de Servicio</TableCell>
          <TableCell>Notas Fijas</TableCell>
          <TableCell>Última Visita</TableCell>
          <TableCell>Activo</TableCell>
          <TableCell align="right">Acciones</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {configsCliente.length === 0 ? (
          <TableRow>
            <TableCell colSpan={8} align="center">
              No hay configuraciones recurrentes para este cliente
            </TableCell>
          </TableRow>
        ) : (
          configsCliente.map((config) => (
            <TableRow key={config.id_config}>
              <TableCell>{config.id_config}</TableCell>
              <TableCell>{config.frecuencia}</TableCell>
              <TableCell> {config.dia_preferido ? DIAS_SEMANA.find(d => d.value === config.dia_preferido.toString())?.label || config.dia_preferido : '-'} </TableCell>
              <TableCell>{config.hora_aprox ? dayjs(`2000-01-01T${config.hora_aprox}`).format('hh:mm A') : '-'}</TableCell>
              <TableCell>{config.hoja_servicio_fija?.substring(0, 50) || '-'}</TableCell>
              <TableCell>{config.notas_fijas?.substring(0, 50) || '-'}</TableCell>
              <TableCell> {config.ultima_visita_realizada ? dayjs(config.ultima_visita_realizada).format('DD/MM/YYYY') : 'Sin visitas'} </TableCell>
              <TableCell>{config.activo ? 'Sí' : 'No'}</TableCell>
              <TableCell align="right">
                <IconButton onClick={() => handleOpenConfigModal(config)}>
                  <EditIcon />
                </IconButton>
                <IconButton color="error" onClick={() => handleDeleteConfig(config.id_config)}>
                  <DeleteIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  </TableContainer>
</Box>   

    

      {/* ================================================== */}
      {/* HISTORIAL DE CONSUMO Y DTE */}
      {/* ================================================== */}
      <Box sx={{ mt: 8, mb: 4 }}>
        
        {/* Botón de importación (Permanece siempre visible) */}
        <Button
          variant="contained"
          color="secondary"
          startIcon={<UploadIcon />}
          onClick={() => setOpenImportarDte(true)}
          sx={{ mb: 3 }}
        >
          Importar Documentos Electrónicos (JSON)
        </Button>

        {/* Historial de Consumo (Se oculta si es Cliente Contado) */}
        {formData.nombre_cliente !== 'Cliente Contado' && (
          <>
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              📈 Historial de Consumo
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
              Detalle de todos los servicios y productos facturados al cliente en documentos electrónicos (DTE).
            </Typography>

            <DetalleConsumoCliente idCliente={id} />
          </>
        )}
      </Box>

<Dialog open={openConfigModal} onClose={() => setOpenConfigModal(false)} fullWidth maxWidth="sm">
  <DialogTitle>{selectedConfig ? 'Editar Configuración Recurrente' : 'Nueva Configuración Recurrente'}</DialogTitle>
  <DialogContent>
    <TextField
      select
      fullWidth
      label="Frecuencia"
      value={configFormData.frecuencia}
      onChange={(e) => setConfigFormData({ ...configFormData, frecuencia: e.target.value })}
      sx={{ mt: 2 }}
      required
    >
      {['SEMANAL', 'QUINCENAL', 'MENSUAL', 'CADA_DOS_MESES', 'CADA_TRES_MESES', 'SOLO_CUMPLEANIOS', 'OTRA'].map(f => (
        <MenuItem key={f} value={f}>{f}</MenuItem>
      ))}
    </TextField>

   <FormControl fullWidth sx={{ mt: 2, bgcolor: 'action.hover' }}>
      <InputLabel>Sub-zona (Asignada al Cliente)</InputLabel>
      <Select
        value={formData.id_sub_zona || ''} // Usamos directamente el dato del cliente
        label="Sub-zona (Asignada al Cliente)"
        inputProps={{ readOnly: true }} // Bloquea la edición
      >
        <MenuItem value=""><em>Sin zona asignada</em></MenuItem>
        {subZonas.map(sz => (
          <MenuItem key={sz.id_sub_zona} value={sz.id_sub_zona}>
            {sz.nombre_sub_zona}
          </MenuItem>
        ))}
      </Select>
      <Typography variant="caption" sx={{ ml: 2, mt: 0.5, color: 'text.secondary' }}>
        Para cambiar la zona, edite los Datos Personales del cliente.
      </Typography>
    </FormControl>

    <TextField
      select
      fullWidth
      label="Día preferido"
      value={configFormData.dia_preferido || ''}
      onChange={(e) => setConfigFormData({ ...configFormData, dia_preferido: e.target.value })}
      sx={{ mt: 2 }}
      helperText="Día de la semana preferido para la visita recurrente (opcional)"
    >
      {DIAS_SEMANA.map((dia) => (
        <MenuItem key={dia.value} value={dia.value}>
          {dia.label}
        </MenuItem>
      ))}
    </TextField>

    <TextField
      fullWidth
      label="Hora aproximada"
      type="time"
      value={configFormData.hora_aprox || ''}
      onChange={(e) => setConfigFormData({ ...configFormData, hora_aprox: e.target.value })}
      sx={{ mt: 2 }}
    />

    <TextField
      fullWidth
      multiline
      rows={3}
      label="Hoja de servicio fija"
      value={configFormData.hoja_servicio_fija}
      onChange={(e) => setConfigFormData({ ...configFormData, hoja_servicio_fija: e.target.value })}
      sx={{ mt: 2 }}
      required
    />

    <TextField
      fullWidth
      multiline
      rows={2}
      label="Notas fijas"
      value={configFormData.notas_fijas}
      onChange={(e) => setConfigFormData({ ...configFormData, notas_fijas: e.target.value })}
      sx={{ mt: 2 }}
    />

    <Grid item xs={12}>
      <TextField
        fullWidth
        label="Etiquetas Fijas"
        placeholder="Ej: BANO, VACUNA, DESPARASITANTE"
        value={configFormData.etiquetas_fijas || ''}
        onChange={(e) => setConfigFormData({ ...configFormData, etiquetas_fijas: e.target.value })}
        helperText="Etiquetas que se copiarán automáticamente a cada nuevo domicilio"
        variant="outlined"
        sx={{ mt: 2 }}
      />
    </Grid>

    <TextField
    fullWidth
    label="Fecha de Última Visita"
    value={dayjs(configFormData.ultima_visita_realizada).format('DD [de] MMMM, YYYY')}
    InputProps={{ readOnly: true }}
    sx={{ mt: 2, bgcolor: 'action.hover' }}
    helperText="Actualizado automáticamente por Logística"
  />

    <FormControlLabel
      control={
        <Switch
          checked={configFormData.activo}
          onChange={(e) => setConfigFormData({ ...configFormData, activo: e.target.checked })}
          color="primary"
        />
      }
      label="Activa"
      sx={{ mt: 2 }}
    />
  </DialogContent>
  <DialogActions>
    <Button onClick={() => setOpenConfigModal(false)}>Cancelar</Button>
    <Button variant="contained" onClick={handleSaveConfig}>Guardar</Button>
  </DialogActions>
</Dialog>





      {/* Snackbar Consolidado */}
<Snackbar
  open={snackbar.open}
  autoHideDuration={5000}
  onClose={handleSnackbarClose}
  anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
>
  <Alert 
    onClose={handleSnackbarClose} 
    severity={snackbar.severity}
    variant="filled"
    sx={{ width: '100%' }}
  >
    {snackbar.message}
  </Alert>
</Snackbar>

{/* Modal de Importación de DTEs */}
<ImportarDteModal
  open={openImportarDte}
  onClose={() => setOpenImportarDte(false)}
  idSucursal={formData.id_sucursal}          // se toma del cliente actual
  idCliente={id}                             // ID del cliente que estamos editando
  nombreCliente={formData.nombre_cliente}    // para la nota aclaratoria
  onSuccess={(numeroControl) => {
    // Opcional: mostrar mensaje y refrescar el historial de consumo
    setSnackbar({
      open: true,
      message: `Documento ${numeroControl} importado correctamente`,
      severity: 'success'
    });
    // Si DetalleConsumoCliente tiene un método de refresh, podrías llamarlo aquí
  }}
/>



    </Container>
  </Box>
</LocalizationProvider>
  );
};

export default EditarCliente;